import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CustomButton } from '../Button/Button';
import styles from './NewComment.module.scss';
import { Icon } from '../Icon';
import EmojiPicker from 'emoji-picker-react';
import useOutsideClick from '../../hooks/useOutsideClick';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import useAutosizeTextArea from '../../hooks/useAutosizeTextarea';
import { secondsToTimeString } from '../../utils/TimeUtil';
import { useVideoProgressContext } from '../../context/VideoProgressContext';

const NewComment = ({
  user,
  currentUserName,
  setCurrentUserName,
  comment,
  parentAuthor,
  handleCancel,
  onSubmitComment,
  inReplyTo,
  isEdit,
  isReply,
  replyProcessing,
  showNewCommentForm,
  setShowNewCommentForm,
}) => {
  const replyRef = useRef();
  const replyNameRef = useRef();

  const [newComment, setNewComment] = useState(comment);
  const [nameFieldType, setNameFieldType] = useState('text');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const { t } = useTranslation();
  const [isChatIconShown, setIsChatIconShown] = useState(false);

  const [showNameField, setShowNameField] = useState(currentUserName === null);

  useAutosizeTextArea(replyRef.current, newComment.content, showNewCommentForm?.show);


  const { videoProgress } = useVideoProgressContext();

  const emojiRef = useRef(null);
  useOutsideClick(emojiRef, () => {
    showEmojiPicker && setShowEmojiPicker(false);
  });

  useEffect(() => {
    if (newComment.content === '' && showNewCommentForm.show) {
      replyRef.current.focus();
    }

    const content = parentAuthor ? `@${parentAuthor} ` : comment.content;

    if (user && user.userFullName) {
      setNameFieldType('hidden');
      setNewComment({
        ...newComment,
        authorName: user.userFullName,
        content,
      });
    } else if (currentUserName !== null) {
      setNameFieldType('hidden');
      setNewComment({
        ...newComment,
        authorName: currentUserName,
        content,
      });
    } else {
      setNewComment({ ...newComment, content });
    }

    replyRef.current.setSelectionRange(content.length, content.length);
  }, []);

  useEffect(() => {
    if (showNewCommentForm?.show) {
      replyRef.current.focus();
      replyRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      setShowNameField(currentUserName === null);

      if (showNewCommentForm?.timestamp) {
        const content = `${secondsToTimeString(videoProgress.milliseconds / 1000)} ${comment.content}`

        setNewComment({
          ...newComment,
          content: parentAuthor ? `@${parentAuthor} ` : content,
        });
      }
    }
  }, [showNewCommentForm?.show]);

  const handleChangeInput = e => {
    if (e.target.name === 'content') {
      setNewComment({ ...newComment, content: e.target.value });
    } else if (e.target.name === 'authorName') {
      setNewComment({ ...newComment, authorName: e.target.value });
      setCurrentUserName(e.target.value);
    }
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
  };

  const onEmojiClick = emoji => {
    setNewComment({ ...newComment, content: `${newComment.content}${emoji.emoji}` });
  };

  const handleSubmitComment = () => {
    if (inReplyTo !== null) {
      newComment.parent = inReplyTo;
      newComment.parentAuthor = parentAuthor;
    }
    setNewComment({ ...newComment, content: '' });
    onSubmitComment(newComment);
    setNameFieldType('hidden');
  };

  const hideChatIcon = () => {
    if ($crisp && $crisp?.is && !$crisp.is('chat:opened')) {
      setIsChatIconShown(true);
      $crisp.push(['do', 'chat:hide']);
    }
  };

  const handleSaveComment = () => {
    isChatIconShown && $crisp.push(['do', 'chat:show']);
    handleSubmitComment();
  };

  const handleFocus = (e) => {
    hideChatIcon();
    setShowNewCommentForm({ show: true, timestamp: false });
  };

  const actionBtnText = isEdit ? t('general.save') : t('general.comment.send');

  const cancelRef = useRef(null);
  useOutsideClick(cancelRef, () => {
    if (showNewCommentForm?.show) {
      handleCancel(false);
    }
  });

  const handleAddTimestamp = () => {
    setNewComment({
      ...newComment,
      content: `${newComment.content} ${secondsToTimeString(videoProgress.milliseconds / 1000)}`,
    });
  };

  useEffect(() => {
    if (currentUserName && user === null) {
      setNewComment(prevState => ({ ...prevState, authorName: currentUserName }));
      newComment.authorName;
    }
  }, [currentUserName]);

  return (
    <div
      className={styles.newCommentContainer}
      ref={cancelRef}
      style={inReplyTo !== null || isReply ? { marginLeft: '48px' } : { marginLeft: '0px', marginBottom: '18px' }}
    >
      <div className={styles.newCommandInput}>
        <textarea
          className={styles.commentContent}
          cols="45"
          name="content"
          ref={replyRef}
          value={newComment.content}
          onChange={handleChangeInput}
          placeholder={t('newcomment.add_comment')}
          onFocus={handleFocus}
          onClick={handleFocus}
          style={{ transition: 'height 0.3s ease' }}
          tabIndex={0}
          aria-label={t('newcomment.add_comment')}
        />

        {!isMobile && (showNewCommentForm?.show || newComment.content.trim() !== '') ? (
          <>
            <Icon src="addEmoji" verticalAlign="middle" className={styles.addEmojiIcon} onClick={() => toggleEmojiPicker()} tabIndex={0} aria-label={t('accessibility.more_reactions')} />
            <CustomButton
              label={t('newcomment.add_time')}
              variant="link"
              className={styles.addTimeBtn}
              onClick={handleAddTimestamp}
            />
          </>
        ) : null}
        {isMobile ? null : (
          <div ref={emojiRef} className={styles.emojiPicker} style={{ display: showEmojiPicker ? 'block' : 'none' }}>
            <EmojiPicker onEmojiClick={onEmojiClick} previewConfig={{ showPreview: false }} />
          </div>
        )}
        {(showNewCommentForm?.show || newComment.content.trim() !== '') ? (
          <div className={styles.actionButtonsContainer}>
            <CustomButton
              label={replyProcessing ? t('general.saving') : actionBtnText}
              variant="secondary"
              customStyles={!newComment.authorName || !newComment.content ? { opacity: ' 0.3' } : {}}
              disabled={!newComment.authorName || !newComment.content || replyProcessing}
              className={styles.saveBtn}
              onClick={handleSaveComment}
            />
          </div>
        ) : null}
      </div>
      {showNewCommentForm?.show && user === null && showNameField ? (
        <input
          placeholder={t('general.your_name')}
          maxLength={130}
          className={styles.replyNameField}
          ref={replyNameRef}
          type={nameFieldType}
          name="authorName"
          value={newComment.authorName}
          onChange={e => handleChangeInput(e)}
        />
      ) : null}
    </div>
  );
};

export default NewComment;
